/* eslint-disable jsx-a11y/alt-text */
import styled from '@emotion/styled';
import { useMedia } from '@landsbankinn/ui';
import React, { useEffect, useRef, useState } from 'react';
import { useSpring, animated } from 'react-spring';

const WIDTH = 566;
const HEIGHT = 718;

// house 0-200
// apple 0-240
// lemon 0-500

// coffee blue 86-530
// ice cream 110-800
// basketball 245-860
// laptop 433-1004
// ticket 680-1470
// stroller 640-740
// coffee green 662-1226
// noodles 595-1220
// whalesplash 290-354
// golfball 90-280

const Host = styled.div`
  width: 566px;
  height: 744px;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  position: relative;
  margin-top: -40px;
  margin-bottom: -40px;
`;

const Reel = styled(animated.div)`
  position: relative;
  width: 100%;
  height: 100%;
  will-change: transform;
`;

const Box = styled.div<{
  color: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
}>`
  position: absolute;
  width: ${(props) => ((props.width || 100) / WIDTH) * 100}%;
  height: ${(props) => ((props.height || 100) / HEIGHT) * 100}%;
  margin-bottom: 20px;
  top: ${(props) => ((props.y || 0) / HEIGHT) * 100}%;
  left: ${(props) => ((props.x || 0) / WIDTH) * 100}%;
  transform: translate3d(0px, 0px, 0px);
`;

const frames = 1500;
const distance = 306.82451253;

const interpolateFrames = (
  y: any,
  a1: any,
  a2: any,
  b1: any,
  b2: any,
  neg = false,
) => {
  return y.to((v: any) => {
    return `${
      interpolate(
        -parseFloat(v),
        (a1 / frames) * distance,
        (a2 / frames) * distance,
        b1,
        b2,
      ) * (neg ? -1 : 1)
    }%`;
  });
};

const clamp = (x: number, min: number, max: number) => {
  return Math.max(min, Math.min(max, x));
};

const interpolate = (
  x: number,
  x1: number,
  x2: number,
  y1: number,
  y2: number,
): number => {
  if (x1 === x2) {
    return clamp((y1 + y2) / 2, y1, y2);
  }
  return clamp(((x - x1) * (y2 - y1)) / (x2 - x1) + y1, y1, y2);
};

const BottomWrap = styled(animated.div)`
  position: absolute;
  width: 100%;
  bottom: 0;
  will-change: transform;
`;

function Bottom({ children, from, to, y }: any) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [ratio, setRatio] = useState<number | null>(null);
  const updateRatio = () => {
    if (ref.current && ref.current.parentElement) {
      setRatio(
        ref.current.parentElement.getBoundingClientRect().height /
          ref.current.getBoundingClientRect().height,
      );
    }
  };

  useEffect(() => {
    if (ref.current) {
      const img = ref.current.querySelector<HTMLImageElement>('img');
      if (img) {
        if (img.complete) updateRatio();
        else img.onload = () => updateRatio();
      }
    }
  }, [ref]);

  return (
    <BottomWrap
      ref={ref}
      style={
        ratio && y
          ? { y: interpolateFrames(y, from, to, 0, (ratio - 1) * 100, true) }
          : {}
      }
    >
      {children}
    </BottomWrap>
  );
}
const TopWrap = styled(animated.div)`
  position: absolute;
  width: 100%;
  top: 0;
  will-change: transform;
`;

function Top({ children, from, to, y }: any) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [ratio, setRatio] = useState<number | null>(null);
  const updateRatio = () => {
    if (ref.current && ref.current.parentElement) {
      setRatio(
        ref.current.parentElement.getBoundingClientRect().height /
          ref.current.getBoundingClientRect().height,
      );
    }
  };

  useEffect(() => {
    if (ref.current) {
      const img = ref.current.querySelector<HTMLImageElement>('img');
      if (img) {
        if (img.complete) updateRatio();
        else img.onload = () => updateRatio();
      }
    }
  }, [ref]);

  return (
    <TopWrap
      ref={ref}
      style={
        ratio && y
          ? { y: interpolateFrames(y, from, to, 0, (ratio - 1) * 100) }
          : {}
      }
    >
      {children}
    </TopWrap>
  );
}

const Right = styled(animated.div)`
  position: absolute;
  height: 100%;
  right: 0;
`;

const GradientTop = styled.div`
  position: absolute;
  top: 6px;
  left: 0;
  right: 0;
  height: 34px;
  z-index: 99;
  background-image: linear-gradient(
    0deg,
    rgba(255, 255, 225, 0) 0%,
    rgba(255, 255, 255, 1) 75%
  );
`;

const GradientBottom = styled(GradientTop)`
  top: auto;
  bottom: -1px;
  background-image: linear-gradient(
    180deg,
    rgba(255, 255, 225, 0) 0%,
    rgba(255, 255, 255, 1) 75%
  );
`;

export function VerticalReel() {
  const isMobile = useMedia('sm', true);

  const { y } = useSpring({
    loop: true,
    from: { y: '-0%' },
    to: { y: `-${distance}%` },
    config: {
      duration: 25000,
      easing: (t) => t, // linear
    },
  });

  if (isMobile) {
    return null;
  }

  return (
    <Host>
      <GradientTop />
      <GradientBottom />
      <Reel
        style={{
          y,
        }}
      >
        <Box color="#a0a0a0" x={0} y={-25} width={182} height={195}>
          <img src="/svgs/Flokkar_Matvara.svg" />
        </Box>
        <Box color="#a0a0a0" x={220} y={30} width={140} height={140}>
          <img src="/svgs/Record.svg" />
        </Box>
        <Box color="#a0a0a0" x={396} y={0} width={170} height={170}>
          <img src="/svgs/Flokkar_Billinn.svg" />
        </Box>
        <Box color="#a0a0a0" x={0} y={184} width={154} height={178}>
          <img src="/svgs/Flokkar_Ferdalog.svg" />
        </Box>
        <Box color="blue" x={160} y={170} width={260} height={230}>
          <Top from={0} to={200} y={y}>
            <img src="/svgs/Flokkar_Heimilid.svg" />
          </Top>
        </Box>
        <Box color="blue" x={420} y={170} width={146} height={230}>
          <Bottom from={0} to={240} y={y}>
            <img src="/svgs/Flokkar_Heilsan.svg" />
          </Bottom>
        </Box>
        <Box color="#a0a0a0" x={0} y={415} width={130} height={130}>
          <img src="/svgs/Dragonfruit.svg" />
        </Box>
        <Box color="blue" x={147} y={450} width={132} height={395}>
          <Top from={90} to={280} y={y}>
            <img src="/svgs/Golfball.svg" />
          </Top>
          <img
            src="/svgs/Golfgreen.svg"
            style={{ width: '100%', height: '102.6%' }}
          />
        </Box>
        <Box color="#a0a0a0" x={315} y={400} width={214} height={316}>
          <img src="/svgs/Sub.svg" />
        </Box>
        <Box color="blue" x={0} y={545} width={130} height={350}>
          <Bottom from={0} to={500} y={y}>
            <img src="/svgs/Lemon.svg" />
          </Bottom>
        </Box>
        <Box color="#a0a0a0" x={279} y={772} width={130} height={130}>
          <img src="/svgs/Tire_quarter.svg" />
        </Box>
        <Box color="blue" x={431} y={680} width={135} height={226}>
          <Bottom from={86} to={530} y={y}>
            <img src="/svgs/Coffee_blue.svg" />
          </Bottom>
        </Box>
        <Box color="blue" x={0} y={914} width={260} height={177}>
          <animated.div
            id="dot"
            style={{
              position: 'absolute',
              bottom: 96,
              width: '6.153846154%',
              height: '9.039548023%',
              left: '52%',
              y: interpolateFrames(y, 290, 381, 0, 1000, true),
              zIndex: 80,
            }}
          >
            <img src="/svgs/Whale_03-01.svg" />
          </animated.div>
          <animated.div
            id="squish"
            style={{
              position: 'absolute',
              bottom: 0,
              width: `${(60 / 260) * 100}%`,
              height: `${(110 / 177) * 100}%`,
              left: '50%',
              marginLeft: `-${(60 / 260) * 50}%`,
              y: interpolateFrames(y, 290, 354, 0, 118, true),
              zIndex: 90,
            }}
          >
            <img src="/svgs/Whale_02.svg" />
          </animated.div>
          <img
            src="/svgs/Whale_01.svg"
            style={{ position: 'relative', zIndex: 100 }}
          />
        </Box>
        <Box color="#a0a0a0" x={434} y={919} width={132} height={132}>
          <img src="/svgs/Watermelon.svg" />
        </Box>
        <Box color="blue" x={279} y={902} width={130} height={356}>
          <Bottom from={110} to={800} y={y}>
            <img src="/svgs/IceCream.svg" />
          </Bottom>
        </Box>
        <Box color="#a0a0a0" x={0} y={1120} width={260} height={262}>
          <img src="/svgs/Basketball.svg" />
        </Box>
        <Box color="blue" x={434} y={1051} width={132} height={265}>
          <Bottom from={272} to={810} y={y}>
            <img src="/svgs/Plant.svg" />
          </Bottom>
        </Box>
        <Box color="#a0a0a0" x={434} y={1316} width={132} height={132}>
          <img src="/svgs/Fruitbox.svg" />
        </Box>
        <Box color="#a0a0a0" x={276} y={1259} width={131} height={194}>
          <img
            src="/svgs/Paperclip.svg"
            style={{ height: '100%', width: '100%' }}
          />
        </Box>
        <Box color="blue" x={0} y={1402} width={260} height={189}>
          <Top from={433} to={1004} y={y}>
            <img src="/svgs/Laptop.svg" />
          </Top>
        </Box>
        <Box color="blue" x={260} y={1445} width={306} height={282}>
          <Right
            style={{
              x: interpolateFrames(y, 640, 740, 0, 10, true),
            }}
          >
            <img src="/svgs/Stroller.svg" style={{ height: '100%' }} />
          </Right>
        </Box>
        <Box color="#a0a0a0" x={0} y={1591} width={140} height={140}>
          <img src="/svgs/Record.svg" />
        </Box>
        <Box color="blue" x={0} y={1649} width={260} height={292}>
          <Bottom from={595} to={1220} y={y}>
            <img src="/svgs/Noodles.svg" />
          </Bottom>
        </Box>
        <Box color="blue" x={0} y={1915} width={148} height={196}>
          <Bottom from={820} to={1426} y={y}>
            <img src="/svgs/Flokkar_Gjafavara.svg" />
          </Bottom>
        </Box>
        <Box color="blue" x={276} y={1727} width={131} height={231}>
          <Top from={662} to={1226} y={y}>
            <img src="/svgs/Coffee_green.svg" />
          </Top>
        </Box>
        <Box color="blue" x={435} y={1737} width={131} height={438}>
          <Top from={680} to={1470} y={y}>
            <img src="/svgs/Ticket.svg" />
          </Top>
        </Box>
        <Box color="#a0a0a0" x={181} y={1958} width={226} height={245}>
          <img src="/svgs/Flokkar_Fatnadur.svg" />
        </Box>

        {/* offset for loop */}

        <Box color="#a0a0a0" x={0} y={-25 + 2203} width={182} height={195}>
          <img src="/svgs/Flokkar_Matvara.svg" />
        </Box>
        <Box color="#a0a0a0" x={220} y={30 + 2203} width={140} height={140}>
          <img src="/svgs/Record.svg" />
        </Box>
        <Box color="#a0a0a0" x={396} y={0 + 2203} width={170} height={170}>
          <img src="/svgs/Flokkar_Billinn.svg" />
        </Box>
        <Box color="#a0a0a0" x={0} y={184 + 2203} width={154} height={178}>
          <img src="/svgs/Flokkar_Ferdalog.svg" />
        </Box>
        <Box color="blue" x={160} y={170 + 2203} width={260} height={230}>
          <Top>
            <img src="/svgs/Flokkar_Heimilid.svg" />
          </Top>
        </Box>
        <Box color="blue" x={420} y={170 + 2203} width={146} height={230}>
          <Bottom>
            <img src="/svgs/Flokkar_Heilsan.svg" />
          </Bottom>
        </Box>
        <Box color="#a0a0a0" x={0} y={415 + 2203} width={130} height={130}>
          <img src="/svgs/Dragonfruit.svg" />
        </Box>
        <Box color="blue" x={147} y={450 + 2203} width={132} height={395}>
          <Top>
            <img src="/svgs/Golfball.svg" />
          </Top>
          <img src="/svgs/Golfgreen.svg" />
        </Box>
        <Box color="#a0a0a0" x={315} y={400 + 2203} width={214} height={316}>
          <img src="/svgs/Sub.svg" />
        </Box>
        <Box color="blue" x={0} y={545 + 2203} width={130} height={350}>
          <Bottom>
            <img src="/svgs/Lemon.svg" />
          </Bottom>
        </Box>
      </Reel>
    </Host>
  );
}
